import {
    PaymentAuthorisationType,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult,
    WalletProvider
} from "../payment";
import {PaymentModel} from "../payment.model";
import {ParamConfig} from "../../config-support/param-config.service";
import {WalletPaymentProcessor} from "./wallet-payment-processor";
import {AdyenApplePaymentData, AdyenGooglePaymentData} from "../adyen";

export class AdyenWalletPaymentProcessor extends WalletPaymentProcessor {
    private adyenCheckout: any;
    private adyenGooglePay: any;
    private adyenApplePay: any;
    private submissionResult: WalletPaymentSubmissionResult = new WalletPaymentSubmissionResult(PaymentAuthorisationType.ADYEN_PAYMENT_METHOD);

    public paymentMethodsResponse: any;

    constructor(paymentModel: PaymentModel) {
        super(paymentModel);
        this.paymentMethodsResponse = JSON.parse(this.paymentModel.paymentMethods);
    }

    public async init(): Promise<WalletPaymentProcessor> {
        this.quickPayAvailable = false;
        if (!this.paymentModel.paymentPlatformPublicKey) {
            return undefined;
        }

        // noinspection JSUnresolvedReference
        try {
            this.adyenCheckout = await (<any>window).AdyenWeb.AdyenCheckout({
                clientKey: this.paymentModel.paymentPlatformPublicKey,
                environment: this.paymentModel.paymentPlatformLiveMode ? "live-au" : "test",
                locale: "en_US",
                countryCode: "AU",
                paymentMethodsResponse: this.paymentMethodsResponse
            });

            this.buildApplePayComponent();
            this.buildGooglePayComponent();
        } catch (error) {
            console.error("Adyen wallet payment initialisation failed", error);
            return undefined;
        }

        const paramConfig: ParamConfig = await this.paymentModel.paramConfigService.getConfig();
        this.walletProvider = paramConfig.forcePay === "apple" ? WalletProvider.APPLE_PAY
            : paramConfig.forcePay === "google" ? WalletProvider.GOOGLE_PAY
                : undefined;
        if (this.walletProvider == undefined && this.adyenApplePay) {
            try {
                await this.adyenApplePay.isAvailable();
                this.walletProvider = WalletProvider.APPLE_PAY;
            } catch (error) {
                console.log("Adyen apple pay not available", error);
            }
        }
        if (this.walletProvider == undefined && this.adyenGooglePay) {
            try {
                await this.adyenGooglePay.isAvailable();
                this.walletProvider = WalletProvider.GOOGLE_PAY;
            } catch (error) {
                console.log("Adyen google pay not available", error);
            }
        }
        console.log("Wallet payment provider =", this.walletProvider);
        if (this.walletProvider == undefined) {
            return undefined;
        }
        this.quickPayAvailable = true;
        return this;
    }

    private buildApplePayComponent(amount: number = 1): void {
        if (!this.paymentModel.responsiveService.isIos) {
            return;
        }

        const applePayMethod: any = this.paymentMethodsResponse.paymentMethods.find((paymentMethod: any) => paymentMethod.name === "Apple Pay");
        const applePayConfiguration = applePayMethod && applePayMethod.configuration ? JSON.parse(JSON.stringify(applePayMethod.configuration)) : {};
        if (this.paymentModel.paymentPlatformLiveMode || !applePayConfiguration.merchantName) {
            applePayConfiguration.merchantName = this.paymentModel.sessionModel.currentApp.googlePayMerchantName;
        }
        // applePayMethod.brands = ["amex", "maestro", "mc", "visa"];

        // noinspection JSUnusedGlobalSymbols,JSUnusedLocalSymbols
        this.adyenApplePay = new (<any>window).AdyenWeb.ApplePay(this.adyenCheckout, {
            configuration: applePayConfiguration,
            existingPaymentMethodRequired: true,
            amount: {
                value: Math.round(amount * 100),
                currency: "AUD"
            },
            countryCode: "AU",
            totalPriceLabel: "Total",
            requiredShippingContactFields: [
                ...this.paymentModel.requestPayerEmail ? ["email"] : [],
                ...this.paymentModel.requestPayerPhone ? ["phone"] : [],
                ...this.paymentModel.requestPayerName ? ["name"] : [],
            ],
            requiredBillingContactFields: [
                // ...this.paymentModel.requestPayerPostcode ? ["postalAddress"] : []
            ],
            onAuthorized: (
                paymentData: AdyenApplePaymentData,
                actions: {
                    resolve: () => void,
                    reject: () => void
                }
            ) => {
                if (paymentData.authorizedEvent) {
                    if (paymentData.authorizedEvent.payment && paymentData.authorizedEvent.payment.shippingContact) {
                        this.submissionResult.payerName = (paymentData.authorizedEvent.payment.shippingContact.givenName + " " + paymentData.authorizedEvent.payment.shippingContact.familyName).trim();
                        this.submissionResult.payerPhone = paymentData.authorizedEvent.payment.shippingContact.phoneNumber;
                        this.submissionResult.payerEmail = paymentData.authorizedEvent.payment.shippingContact.emailAddress;
                    }
                    if (paymentData.billingAddress) {
                        // this.submissionResult.payerPostcode = paymentData.billingAddress.postalCode;
                    }
                }
                actions.resolve();
            },
            onSubmit: (
                state: {
                    data: any,
                    isValid: boolean
                },
                elementRef: any,
                actions: {
                    resolve: (resultCode: string) => void,
                    reject: () => void
                }
            ) => {
                if (state.isValid) {
                    actions.resolve("Received");
                    this.submissionResult.token = btoa(JSON.stringify(state.data));
                    // noinspection JSIgnoredPromiseFromCall
                    this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider);
                    this.resolveSubmission(this.submissionResult);
                    this.rejectSubmission = undefined;
                    this.resolveSubmission = undefined;
                } else {
                    actions.reject();
                }
            },
            onError: (
                error: {
                    name: string,
                    message: string,
                    cause: string,
                    sourceURL: string,
                    stack: string
                },
                elementRef: any
            ) => {
                // noinspection JSIgnoredPromiseFromCall
                this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider);
                if (this.rejectSubmission) {
                    this.rejectSubmission(new WalletPaymentSubmissionFailure(error.name === "CANCEL" ? WalletPaymentErrorType.APPLE_PAY_CANCELLED : WalletPaymentErrorType.PROVIDER_ERROR,
                        error.name, typeof error.cause === "string" ? error.cause : error.message, typeof error.cause === "string" ? error.message : undefined));
                    this.rejectSubmission = undefined;
                    this.resolveSubmission = undefined;
                }
            }
        });
    }

    private buildGooglePayComponent(amount: number = 1): void {
        const googlePayMethod: any = this.paymentMethodsResponse.paymentMethods.find((paymentMethod: any) => paymentMethod.name === "Google Pay");
        const googlePayConfiguration = googlePayMethod && googlePayMethod.configuration ? JSON.parse(JSON.stringify(googlePayMethod.configuration)) : {};
        if (this.paymentModel.paymentPlatformLiveMode || !googlePayConfiguration.merchantName) {
            googlePayConfiguration.merchantName = this.paymentModel.sessionModel.currentApp.googlePayMerchantName;
        }

        // noinspection JSUnusedGlobalSymbols,JSUnusedLocalSymbols
        this.adyenGooglePay = new (<any>window).AdyenWeb.GooglePay(this.adyenCheckout, {
            configuration: googlePayConfiguration,
            existingPaymentMethodRequired: true,
            amount: {
                value: Math.round(amount * 100),
                currency: "AUD"
            },
            countryCode: "AU",
            environment: this.paymentModel.paymentPlatformLiveMode ? "PRODUCTION" : "TEST",
            emailRequired: this.paymentModel.requestPayerEmail,
            billingAddressRequired: this.paymentModel.requestPayerPhone || this.paymentModel.requestPayerName || this.paymentModel.requestPayerPostcode,
            billingAddressParameters: {
                format: "MIN",
                phoneNumberRequired: this.paymentModel.requestPayerPhone
            },
            onAuthorized: (
                paymentData: AdyenGooglePaymentData,
                actions: {
                    resolve: () => void,
                    reject: () => void
                }
            ) => {
                if (paymentData.authorizedEvent) {
                    this.submissionResult.payerEmail = paymentData.authorizedEvent.email;
                    if (paymentData.authorizedEvent.paymentMethodData && paymentData.authorizedEvent.paymentMethodData.info && paymentData.authorizedEvent.paymentMethodData.info.billingAddress) {
                        this.submissionResult.payerPhone = paymentData.authorizedEvent.paymentMethodData.info.billingAddress.phoneNumber;
                        this.submissionResult.payerName = paymentData.authorizedEvent.paymentMethodData.info.billingAddress.name;
                    }
                    if (paymentData.billingAddress) {
                        this.submissionResult.payerPostcode = paymentData.billingAddress.postalCode;
                    }
                }
                actions.resolve();
            },
            onSubmit: (
                state: {
                    data: any,
                    isValid: boolean
                },
                elementRef: any,
                actions: {
                    resolve: (resultCode: string) => void,
                    reject: () => void
                }
            ) => {
                if (state.isValid) {
                    actions.resolve("Received");
                    this.submissionResult.token = btoa(JSON.stringify(state.data));
                    // noinspection JSIgnoredPromiseFromCall
                    this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider);
                    this.resolveSubmission(this.submissionResult);
                    this.rejectSubmission = undefined;
                    this.resolveSubmission = undefined;
                } else {
                    actions.reject();
                }
            },
            onError: (
                error: {
                    name: string,
                    cause: {
                        statusCode: string
                    }
                },
                elementRef: any
            ) => {
                // noinspection JSIgnoredPromiseFromCall
                this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider);
                if (this.rejectSubmission) {
                    this.rejectSubmission(new WalletPaymentSubmissionFailure(error.name === "CANCEL" ? WalletPaymentErrorType.GOOGLE_PAY_CANCELLED : WalletPaymentErrorType.PROVIDER_ERROR,
                        undefined, error.name, error.cause.statusCode));
                    this.rejectSubmission = undefined;
                    this.resolveSubmission = undefined;
                }
            }
        });
    }

    public async submit(amount: number): Promise<WalletPaymentSubmissionResult> {
        // noinspection ES6MissingAwait
        this.paymentModel.analyticsService.trackWalletPaymentRequestStarted(this.walletProvider);

        if (this.resolveSubmission) {
            this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.INVALID_STATE));
            await this.init();
        }

        return new Promise<WalletPaymentSubmissionResult>((resolve: (value?: WalletPaymentSubmissionResult | PromiseLike<WalletPaymentSubmissionResult>) => void, reject: (reason?: any) => void) => {
            this.resolveSubmission = resolve;
            this.rejectSubmission = reject;
            if (this.walletProvider == WalletProvider.APPLE_PAY) {
                this.buildApplePayComponent(amount);
                this.adyenApplePay.submit();
            } else if (this.walletProvider == WalletProvider.GOOGLE_PAY) {
                this.buildGooglePayComponent(amount);
                this.adyenGooglePay.submit();
            }
        });
    }
}


// WEBPACK FOOTER //
// ./src/app/payment/wallet-payment/adyen-wallet-payment-processor.ts